import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES, PARENT_HUB_PAGES } from '../../../../routing/routeConfiguration';
import { isParentUser, isProviderUser } from '../../../../util/onboarding';
import { isAdmin } from '../../../../util/mfa';
import TopbarSearchPanel from '../TopbarSearchPanel/TopbarSearchPanel';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import { FormattedMessage } from '../../../../util/reactIntl';

import css from './TopbarDesktop.module.css';

// KidHive desktop topbar (Figma 0-6785): logo, an auth-only greeting, the search pill
// (TopbarSearchPanel), a mail icon, and the profile block (avatar + name + chevron). Logged-out
// users see no greeting and no avatar (a Log in link instead). The mail icon links the Inbox; the
// profile menu mirrors the mobile drawer's destinations.

const IconMail = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);
const IconChevron = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="m7 10 5 5 5-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout, inboxTab, intl }) => {
  const isParent = isParentUser(currentUser);
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    // The parent "Profile" entry stays active anywhere in the hub (Parent info / Payment / Security).
    const isParentHub = page === 'ParentInfoPage' && PARENT_HUB_PAGES.includes(currentPage);
    const isInboxPage = page === 'InboxPage' && currentPage?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isParentHub || isInboxPage
      ? css.currentPage
      : null;
  };
  const firstName = currentUser?.attributes?.profile?.firstName;

  // Parents get a clean hub menu: Profile -> the hub, Settings -> Account & security, Log out. Other
  // userTypes keep the stock destinations (Inbox, Your listings, Profile settings, Account, Log out).
  const menuItems = isParent
    ? [
        <MenuItem key="ParentInfoPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ParentInfoPage'))}
            name="ParentInfoPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="BookingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('BookingsPage'))}
            name="BookingsBasePage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.bookingsLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="AccountSecurityPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSecurityPage'))}
            name="AccountSecurityPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.settingsLink" />
          </NamedLink>
        </MenuItem>,
      ]
    : [
        // Providers live in the dashboard hub -- the topbar menu (shown on the class preview) is
        // just Dashboard, Account, Log out. Profile settings is a parent page; a provider's public
        // presence is managed in the hub Account.
        <MenuItem key="ProviderDashboardPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProviderDashboardPage'))}
            name="ProviderDashboardPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.dashboardLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="ProviderAccountBasePage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProviderAccountBasePage'))}
            name="ProviderAccountBasePage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.providerAccountLink" />
          </NamedLink>
        </MenuItem>,
      ].filter(Boolean);

  // Shared informational entries (client: About + Legal reachable while logged in). "Legal" opens
  // the first legal document; every legal page's Related-documents nav walks the rest.
  // About + Legal moved to the shared app footer (client ask: consistent + a leaner menu).
  const aboutLegalItems = [];

  // Admins (metadata.role) get a direct door to the admin area, whatever their userType.
  const adminItemMaybe = isAdmin(currentUser)
    ? [
        <MenuItem key="AdminPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AdminPage'))}
            name="AdminPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.adminLink" />
          </NamedLink>
        </MenuItem>,
      ]
    : [];

  return (
    <Menu skipFocusOnNavigation={true} contentPosition="left">
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        {firstName ? <span className={css.profileName}>{firstName}</span> : null}
        <IconChevron className={css.profileChevron} />
      </MenuLabel>
      <MenuContent rootClassName={css.profileMenuContent} contentClassName={css.profileMenuList}>
        {[
          ...menuItems,
          ...aboutLegalItems,
          ...adminItemMaybe,
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>,
        ]}
      </MenuContent>
    </Menu>
  );
};

/**
 * KidHive desktop topbar (Figma 0-6785).
 * @component
 */
const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    intl,
    isAuthenticated,
    onLogout,
    inboxTab,
    notificationCount = 0,
  } = props;
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const authedClientSide = mounted && isAuthenticated;
  const firstName = currentUser?.attributes?.profile?.firstName;
  // The search panel + greeting are the parent browse chrome; providers (whose search routes
  // redirect to the dashboard, where the greeting lives) get a slim topbar: logo, mail, profile
  // menu, spread edge to edge.
  const isProvider = isProviderUser(currentUser);
  const classes = classNames(rootClassName || css.root, className, {
    [css.rootSlim]: isProvider,
  });

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={css.brand}>
        <LinkedLogo
          id="logo-topbar-desktop"
          rootClassName={css.logoLink}
          logoClassName={css.logoBox}
          logoImageClassName={css.logoImg}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName: '' })}
        />

        {authedClientSide && firstName && !isProvider ? (
          <div className={css.greeting}>
            <span className={css.greetingHi}>Hi, {firstName}</span>
            <span className={css.greetingWelcome}>Welcome Back</span>
          </div>
        ) : null}
      </div>

      {isProvider ? null : <TopbarSearchPanel />}

      <div className={css.actions}>
        {/* Messages is signed-in only: for a signed-out visitor (open browse) the inbox is gated and
            would just bounce them home. Keyed on authedClientSide (post-mount), so SSR and the
            anonymous hydration pass both render without it -- no hydration mismatch. */}
        {authedClientSide ? (
          <NamedLink
            name="InboxPage"
            params={{ tab: inboxTab }}
            className={css.iconButton}
            aria-label="Messages"
          >
            <IconMail className={css.navIcon} />
            {notificationCount > 0 ? <span className={css.notificationDot} /> : null}
          </NamedLink>
        ) : null}

        {authedClientSide ? (
          <ProfileMenu
            currentPage={currentPage}
            currentUser={currentUser}
            onLogout={onLogout}
            inboxTab={inboxTab}
            intl={intl}
          />
        ) : (
          // Signed out (and SSR): Providers (jumps to the homepage's provider section, whose id is
          // 'for-providers' in SectionForProviders), Log in and Sign up.
          <>
            <NamedLink
              name="LandingPage"
              to={{ hash: '#for-providers' }}
              className={css.providersLink}
            >
              <FormattedMessage id="TopbarDesktop.providers" />
            </NamedLink>
            <NamedLink name="LoginPage" className={css.loginLink}>
              <FormattedMessage id="TopbarDesktop.login" />
            </NamedLink>
            <NamedLink name="SignupPage" className={css.loginButton}>
              <FormattedMessage id="TopbarDesktop.signup" />
            </NamedLink>
          </>
        )}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
